import React from "react";
import { LegendKey } from "../../models/chart_model";
import { COST_UNIT } from "../../Units";
import { getTimeRange } from "../../pages/Dashboard/Exchange3/Chart";

export interface LegendStats {
  name: string;
  min: number;
  max: number;
  avg: number;
  total: number;
  minExtra: Record<string, any>;
  maxExtra: Record<string, any>;
  minPrice?: number;
  maxPrice?: number;
  wtdAvg?: number;
}

export interface LegendSummarySlotTableProps {
  legends: LegendKey[];
  avgData: any[];
  minMaxData: any[];
  extraKeys: string[];
  extraLabels: string[];
  unit?: "mw" | "mwh" | "mus" | string;
  avgTableData?: any[];
  block_key?: string;
  priceLegends?: Array<LegendKey | null>;
  showTotal?: boolean;
  showWtdAvg?: boolean;
  showTotalEmpty?: boolean;
  volumeUnitKey?: string;
  priceType?: "simple_avg" | "wt_avg";
}

const formatLegendName = (legend: LegendKey, fallbackUnit?: string) => {
    let suffix = "";
  if (legend?.unit) {
      suffix = legend.unit;
    } else {
      const suffixDict = {
        Price: COST_UNIT,
        Percent: "%",
        "%": "%",
        Cost: "Rs",
        Revenue: "Rs",
      };

    const matchedKey = Object.keys(suffixDict).find((key) =>
        legend.name?.toString().includes(key)
      );

    suffix = matchedKey
      ? suffixDict[matchedKey as keyof typeof suffixDict]
      : legend?.unit || fallbackUnit || "";
    }

    return `${legend.name} (${suffix})`;
};

const calculateLegendStatsSlot = (
  unit: string,
  legends: LegendKey[],
  avgData: any[],
  minMaxData: any[],
  extraKeys: string[] = [],
  avgTableData: any[] = [],
  showWtdAvg: boolean = false,
  volumeUnitKey: string = "mwh"
): LegendStats[] => {
  return legends.map((legend) => {
    const { dataKey } = legend;

    let minVal = Infinity;
    let maxVal = -Infinity;
    let minRecord: any = null;
    let maxRecord: any = null;

    minMaxData.forEach((item) => {
      const value = Number(item[dataKey]);
      if (isNaN(value)) return;

      if (value < minVal) {
        minVal = value;
        minRecord = item;
      }
      if (value > maxVal) {
        maxVal = value;
        maxRecord = item;
      }
    });

    let avgTotal = 0;
    let avgCount = 0;
    let wtdAvgTotal = 0;
    let wtdAvgCount = 0;

    avgData.forEach((item) => {
      const value = Number(item[dataKey]);
      if (isNaN(value)) return;
      avgTotal += value;
      avgCount++;

      if (showWtdAvg) {
        const lowerKey = dataKey.toLowerCase();
        const isActualPrice =
          lowerKey.endsWith("_price") && !lowerKey.includes("prev_year");

        if (isActualPrice) {
          const volumeKey = dataKey.replace(
            /_price$/i,
            `_mcv_${volumeUnitKey}`
          );
          const volume = Number(item[volumeKey]);

          if (!isNaN(volume) && volume > 0 && !isNaN(value)) {
            wtdAvgTotal += volume * value;
            wtdAvgCount += volume;
          }
        }
      }
    });

    let total = 0;
    avgTableData.forEach((item) => {
      const value = Number(item[dataKey]);
      if (!isNaN(value)) {
      total += value;
      }
    });

    if (typeof dataKey === "string" && dataKey.endsWith("_mw")) {
      total = total / 4;
    }

    const minExtra: Record<string, any> = {};
    const maxExtra: Record<string, any> = {};
    extraKeys.forEach((key) => {
      minExtra[key] = minRecord ? minRecord[key] : "-";
      maxExtra[key] = maxRecord ? maxRecord[key] : "-";
    });

    return {
      name: formatLegendName(legend, unit),
      min: minVal === Infinity ? 0 : minVal,
      max: maxVal === -Infinity ? 0 : maxVal,
      avg: avgCount > 0 ? avgTotal / avgCount : 0,
      total,
      minExtra,
      maxExtra,
      unit: legend.unit,
      wtdAvg:
        showWtdAvg && wtdAvgCount > 0 ? wtdAvgTotal / wtdAvgCount : undefined,
    };
  });
};

const LegendSummarySlotTable: React.FC<LegendSummarySlotTableProps> = ({
  legends,
  avgData,
  minMaxData,
  extraKeys,
  extraLabels,
  unit = "MWh",
  avgTableData = [],
  block_key,
  priceLegends,
  showTotal = true,
  showWtdAvg = false,
  showTotalEmpty = false,
  volumeUnitKey = "mwh",
  priceType = "simple_avg",
}) => {
  const extras = extraKeys.map((key, idx) => ({
    key,
    label: extraLabels[idx] || key,
  }));

  const stats = calculateLegendStatsSlot(
    unit,
    legends,
    avgData,
    minMaxData,
    extraKeys,
    avgTableData,
    showWtdAvg,
    volumeUnitKey
  );

  const hasPriceLegends = Boolean(priceLegends?.some(Boolean));
  const firstPriceLegend = priceLegends?.find(
    (legend): legend is LegendKey => Boolean(legend)
  );
  const priceUnitLabel = hasPriceLegends
    ? firstPriceLegend?.unit || "Price"
    : "";

  const statsWithPrice = stats.map((stat, index) => {
    if (priceLegends && priceLegends[index]) {
      const priceKey = priceLegends[index].dataKey;
      let minPrice = Infinity;
      let maxPrice = -Infinity;
      minMaxData.forEach((item) => {
        const price = Number(item[priceKey]);
        if (!isNaN(price)) {
          if (price < minPrice) minPrice = price;
          if (price > maxPrice) maxPrice = price;
        }
      });

      return {
        ...stat,
        minPrice: minPrice === Infinity ? 0 : minPrice,
        maxPrice: maxPrice === -Infinity ? 0 : maxPrice,
      };
    }

    return stat;
  });

  const adjustedStats = statsWithPrice;
  const groupColumnCount = (hasPriceLegends ? 2 : 1) + extras.length;
  const valueUnitLabel = unit === "MW" ? "MW" : unit === "MWh" ? "MWh" : unit;

  return (
    <div className="overflow-x-auto capitalize">
      <table className="min-w-full text-nowrap ">
        <thead className="sticky top-0 z-10 ">
          <tr className="relative after:content-[''] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-px after:bg-[#fdd3bb]">
            <th
              rowSpan={2}
              className="py-2 px-6 text-center z-10 relative after:content-[''] border-0 after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#fdd3bb]"
            >
              Name
            </th>
            <th
              colSpan={groupColumnCount}
              className="py-2 px-6 text-center text-[#34656D] border-0 relative after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#fdd3bb]"
            >
              Minimum
            </th>
            <th
              colSpan={groupColumnCount}
              className="py-2 px-6 text-center text-[#34656D] border-0 relative after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#fdd3bb]"
            >
              Maximum
            </th>
            <th
              rowSpan={2}
              className={`py-2 px-6 text-center z-10 relative border-0 ${
                showTotal === false
                  ? ""
                  : "after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#fdd3bb]"
              }`}
            >
              Average
            </th>
            {showTotal && (
              <th rowSpan={2} className="py-2 px-6 z-10 border-0 text-center">
                Total
              </th>
            )}
          </tr>
          <tr className="text-gray-700">
            <th className="py-2 px-6 text-center border-0 relative after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#fdd3bb]">
              Value
            </th>
            {hasPriceLegends && (
              <th className="py-2 px-6 text-center border-0 relative after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#fdd3bb]">
                {priceUnitLabel}
              </th>
            )}
            {extras.map((extra, idx) => (
              <th
                key={`min-extra-header-${idx}`}
                className="py-2 px-6 text-center capitalize border-0 relative after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#fdd3bb]"
              >
                {extra.label}
              </th>
            ))}
            <th className="py-2 px-6 text-center border-0 relative after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#fdd3bb]">
              Value
            </th>
            {hasPriceLegends && (
              <th className="py-2 px-6 text-center border-0 relative after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#fdd3bb]">
                {priceUnitLabel}
              </th>
            )}
            {extras.map((extra, idx) => (
              <th
                key={`max-extra-header-${idx}`}
                className="py-2 px-6 text-center border-0 relative after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#fdd3bb]"
              >
                {extra.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="text-[#8C8A96] font-normal text-[13px]">
          {adjustedStats.map((stat, index) => (
            <tr
              key={index}
              className="border-b rounded-xl border-gray-200 hover:bg-gray-100 text-center"
            >
              <td className="py-3 px-6 text-center ">
                {(() => {
                  const match = stat.name.match(/(.*)\s*\((.*?)\)$/);
                  if (match) {
                    const [_, nameText, unitText] = match;
                    return (
                      <>
                        <span>{nameText}</span>{" "}
                        <span className="text-[#34656D] font-medium">({unitText})</span>
                      </>
                    );
                  }
                  return stat.name;
                })()}
              </td>
              <td className="py-3 px-6 text-[#34656D] font-medium">
                {stat.min.toFixed(2)}
              </td>
              {hasPriceLegends && (
                <td className="py-3 px-6 ">
                  {stat.minPrice !== undefined ? stat.minPrice.toFixed(2) : "-"}
                </td>
              )}
              {extras.map((extra, idx) => (
                  <td
                    key={`min-extra-${index}-${idx}`}
                    className={`py-3 px-6 ${
                      extra.key === "block" ? "text-[#F1935C] font-medium" : ""
                    }`}
                  >
                    {stat.minExtra[extra.key] !== undefined
                    ? extra.key === block_key
                      ? `${stat.minExtra[extra.key]} (${getTimeRange(stat.minExtra[extra.key])})`
                      : stat.minExtra[extra.key]
                      : "-"}
                  </td>
              ))}
              <td className="py-3 px-6 text-[#34656D] font-medium">
                {stat.max.toFixed(2)}
              </td>
              {hasPriceLegends && (
                <td className="py-3 px-6">
                  {stat.maxPrice !== undefined ? stat.maxPrice.toFixed(2) : "-"}
                </td>
              )}
              {extras.map((extra, idx) => (
                <td
                  key={`max-extra-${index}-${idx}`}
                  className={`py-3 px-6 ${
                    extra.key === "block" ? "text-[#F1935C] font-medium" : ""
                  }`}
                >
                  {stat.maxExtra[extra.key] !== undefined
                    ? extra.key === block_key
                      ? `${stat.maxExtra[extra.key]} (${getTimeRange(stat.maxExtra[extra.key])})`
                      : stat.maxExtra[extra.key]
                    : "-"}
                </td>
              ))}
              <td className="py-3 px-6">
                <span className=" font-medium">
                  {(() => {
                    // For price fields, show weighted average when priceType is wt_avg
                    const isPriceField = priceLegends && priceLegends[index];
                    const shouldShowWtdAvg = 
                      priceType === "wt_avg" && 
                      isPriceField && 
                      stat.wtdAvg !== undefined && 
                      stat.wtdAvg !== null &&
                      !isNaN(stat.wtdAvg);
                    
                    return shouldShowWtdAvg && stat.wtdAvg !== undefined
                      ? stat.wtdAvg.toFixed(2)
                      : stat.avg.toFixed(2);
                  })()}
                </span>
              </td>
              {showTotal && (
                <td className="py-3 px-6">
                  {(() => {
                    const match = stat.name.match(/.*\((.*?)\)$/);
                    let detectedUnit = match ? match[1] : "";

                    if (
                      isNaN(stat.total) ||
                      detectedUnit === "%" ||
                      stat.name.includes("%") ||
                      stat.name.toLowerCase().includes("percent")
                    ) {
                      return <span>-</span>;
                    }

                    if (detectedUnit.toLowerCase() === "mw") {
                      detectedUnit = "MWh";
                    }
                    return (
                      <span className="font-medium">
                        {showTotalEmpty ? (
                          <span>-</span>
                        ) : (
                          <>
                            {stat.total.toFixed(2)} {" ("}
                            {detectedUnit}
                            {")"}
                          </>
                        )}
                      </span>
                    );
                  })()}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default LegendSummarySlotTable;
